import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "question"]
  static values = {
    index:              Number,
    choicePlaceholder:  { type: String, default: "Option %{number}" }
  }

  addQuestion() {
    const template = this.templateTarget.content.cloneNode(true)
    const fieldset = template.querySelector(".question-fieldset")
    const index = this.indexValue

    this.updateFieldNames(fieldset, index)

    const badge = fieldset.querySelector(".question-number")
    if (badge) badge.textContent = index + 1

    this.containerTarget.appendChild(fieldset)
    this.indexValue = index + 1
    this.renumberQuestions()
  }

  removeQuestion(event) {
    const fieldset = event.target.closest(".question-fieldset")
    const destroyInput = fieldset.querySelector(".question-destroy, [name*='_destroy']")

    if (destroyInput) {
      destroyInput.value = "1"
      fieldset.classList.add("hidden")
    } else {
      fieldset.remove()
    }
    this.renumberQuestions()
  }

  changeQuestionType(event) {
    const fieldset = event.target.closest(".question-fieldset")
    const type = event.target.value
    const options = fieldset.querySelector(".question-options")
    if (!options) return

    options.querySelectorAll("[class*='options-']").forEach(el => el.classList.add("hidden"))

    const typeMap = {
      "multiple_choice": ".options-multiple-choice",
      "rating": ".options-rating",
      "nps": ".options-nps",
      "open_text": ".options-open-text"
    }
    const target = options.querySelector(typeMap[type])
    if (target) target.classList.remove("hidden")
  }

  addChoice(event) {
    const container = event.target.closest(".options-multiple-choice")
      ?.querySelector(".choices-container")
    if (!container) return

    const fieldset = event.target.closest(".question-fieldset")
    const questionIndex = this.getQuestionIndex(fieldset)
    const choiceIndex = container.querySelectorAll(".choice-row").length

    const row = document.createElement("div")
    row.className = "flex items-center gap-2 choice-row"
    const placeholder = this.choicePlaceholderValue.replace("%{number}", choiceIndex + 1)
    row.innerHTML = `
      <input type="hidden"
             name="survey[survey_questions_attributes][${questionIndex}][options][choices][${choiceIndex}][key]"
             value="opt_${choiceIndex + 1}"
             class="choice-key" />
      <input type="text"
             name="survey[survey_questions_attributes][${questionIndex}][options][choices][${choiceIndex}][label]"
             class="input input-xs input-bordered flex-1 choice-label"
             placeholder="${this._escape(placeholder)}"
             data-action="input->survey-builder#updateChoiceKey" />
      <button type="button" class="btn btn-xs btn-ghost text-error"
              data-action="survey-builder#removeChoice">x</button>
    `
    container.appendChild(row)
  }

  removeChoice(event) {
    const row = event.target.closest(".choice-row")
    const container = row.parentElement
    if (container.querySelectorAll(".choice-row").length > 1) {
      row.remove()
    }
  }

  updateChoiceKey(event) {
    const label = event.target.value
    const row = event.target.closest(".choice-row")
    const keyInput = row.querySelector(".choice-key")
    if (keyInput) {
      keyInput.value = label
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "_")
        .substring(0, 50) || `opt_${Date.now()}`
    }
  }

  // Private

  updateFieldNames(fieldset, index) {
    const prefix = `survey[survey_questions_attributes][${index}]`

    const typeSelect = fieldset.querySelector(".question-type-select")
    if (typeSelect) typeSelect.name = `${prefix}[question_type]`

    const required = fieldset.querySelector(".question-required")
    if (required) required.name = `${prefix}[is_required]`

    const textField = fieldset.querySelector(".question-text-field")
    if (textField) textField.name = `${prefix}[question_text]`

    const priority = fieldset.querySelector(".question-priority")
    if (priority) {
      priority.name = `${prefix}[priority]`
      priority.value = index
    }

    const destroy = fieldset.querySelector(".question-destroy")
    if (destroy) destroy.name = `${prefix}[_destroy]`

    // Choice inputs
    fieldset.querySelectorAll(".choice-key").forEach((input, ci) => {
      input.name = `${prefix}[options][choices][${ci}][key]`
    })
    fieldset.querySelectorAll(".choice-label").forEach((input, ci) => {
      input.name = `${prefix}[options][choices][${ci}][label]`
    })

    const ratingMin = fieldset.querySelector(".rating-min")
    if (ratingMin) ratingMin.name = `${prefix}[options][min]`
    const ratingMax = fieldset.querySelector(".rating-max")
    if (ratingMax) ratingMax.name = `${prefix}[options][max]`

    const npsUnlikelyLabel = fieldset.querySelector(".nps-unlikely-label")
    if (npsUnlikelyLabel) npsUnlikelyLabel.name = `${prefix}[options][nps_unlikely_label]`
    const npsLikelyLabel = fieldset.querySelector(".nps-likely-label")
    if (npsLikelyLabel) npsLikelyLabel.name = `${prefix}[options][nps_likely_label]`

    const allowMultiple = fieldset.querySelector(".allow-multiple-check")
    if (allowMultiple) allowMultiple.name = `${prefix}[options][allow_multiple]`
  }

  getQuestionIndex(fieldset) {
    const nameInput = fieldset.querySelector("[name*='survey_questions_attributes']")
    if (!nameInput) return this.questionTargets.indexOf(fieldset)
    const match = nameInput.name.match(/\[(\d+)\]/)
    return match ? parseInt(match[1]) : 0
  }

  renumberQuestions() {
    let visible = 0
    this.questionTargets.forEach((q) => {
      if (q.classList.contains("hidden")) return
      visible++
      const badge = q.querySelector(".badge:first-child, .question-number")
      if (badge && !badge.classList.contains("badge-primary")) {
        badge.textContent = visible
      }
    })
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
